import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { 
  Smartphone, 
  MapPin, 
  Plug, 
  Zap, 
  Car,
  Clock,
  DollarSign,
  Star,
  CreditCard,
  Filter,
  CalendarClock,
  History,
  FileText,
  Headphones,
  Bell,
  Map,
  ChevronDown,
  Play
} from "lucide-react";
import { AppleLogo } from "@/components/icons/AppleLogo";
import { motion } from "framer-motion";
import { ScrollReveal, StaggerContainer, StaggerItem, fadeInUp } from "@/components/animations/ScrollAnimations";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SEO, driversPageSchema, faqSchema } from "@/components/SEO";

import mobileApp from "@/assets/mobile_app.png";
import mobileApp2 from "@/assets/mobile_app_2.png";

const appFeatures = [
  { icon: Clock, name: "Real-Time Availability" },
  { icon: DollarSign, name: "Pricing Info" },
  { icon: Star, name: "Favorite Stations" },
  { icon: CreditCard, name: "Flexible Payments" },
  { icon: Filter, name: "Charger Filters" },
  { icon: CalendarClock, name: "Reserve & Waitlist" },
  { icon: History, name: "Charge History" },
  { icon: Map, name: "Location Notes" },
  { icon: FileText, name: "Billing & Invoices" },
  { icon: Headphones, name: "24/7 Support" },
  { icon: Bell, name: "Disconnect Alerts" },
];

const chargingSteps = [
  {
    step: "01",
    title: "Get the App & Join",
    description: "Download our app on the App or Google Play Store, or sign up on our website.",
    icon: Smartphone
  },
  {
    step: "02", 
    title: "Find Station",
    description: "Locate nearby compatible stations in the app and save your favorites.",
    icon: MapPin
  },
  {
    step: "03",
    title: "Plug In",
    description: "Filter by plug type and follow your EV's instructions to connect.",
    icon: Plug
  },
  {
    step: "04",
    title: "Start A Session",
    description: "Activate the station via the app—just ensure your balance is topped up!",
    icon: Zap
  },
  {
    step: "05",
    title: "Drive",
    description: "Charging stops automatically or manually via the app. You're all set!",
    icon: Car
  }
];

const faqs = [
  {
    question: "What can I do with my EvGateway Account?",
    answer: "Creating an evGateway account will give you quick and easy access to the EV charging network and places to plug in. You can sign up using the evGateway App. You can also save charging stations to favorites, manage payments, and track your energy use."
  },
  {
    question: "How do I download the free EvGateway mobile app?",
    answer: "The app is available for iPhone and Android smartphones. Visit the App Store or Google Play and follow the steps displayed on your phone to install."
  },
  {
    question: "What are the device requirements to install EvGateway mobile app?",
    answer: "The EvGateway app is available for iPhone (iOS 5 or later) and Android (version 2.2 and up) smartphones. To submit feedback, please click the \"Contact Us\" button within the app."
  },
  {
    question: "How do I start a charging session using the app or RFID?",
    answer: "There are three ways to start a charging session: 1) Using the EvGateway Mobile App - click on the Remote Start button on the selected charging station. 2) Using your registered mobile number to receive an SMS and reply \"Yes\" to start. 3) Use your EvGateway RFID fob by tapping it on the reader."
  },
  {
    question: "What payment methods are supported?",
    answer: "We accept all major credit cards (Visa, MasterCard, American Express, Discover) and PayPal. You can also enable Auto Reload to automatically top up your balance when it falls below a certain threshold."
  },
  {
    question: "What do the map colors mean?",
    answer: "Green indicates the charger is available, Blue means it's currently in use, and Grey shows the charger is unavailable or offline."
  },
  {
    question: "What happens if a station has a fault?",
    answer: "Our network is monitored 24/7. If a station experiences issues, our team is automatically notified and will work to resolve it. You can also report issues directly through the app and receive support immediately."
  },
  {
    question: "Can I reserve a charging station?",
    answer: "Yes! The app allows you to reserve a station in advance or join a waitlist if all stations are currently occupied. You'll receive a notification when your turn arrives."
  }
];

const Drivers = () => {
  const driverFaqs = faqs.map(f => ({ question: f.question, answer: f.answer }));
  
  // Combined structured data with FAQ schema
  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [
      driversPageSchema,
      faqSchema(driverFaqs)
    ]
  };
  
  return (
    <main className="min-h-screen bg-background">
      <SEO 
        title="EV Driver App - Find Fast Charging Stations Near You"
        description="Download the EVGateway app for seamless EV charging. Find DC Fast Charging stations, check real-time availability, flexible payments, reserve chargers, and enjoy 24/7 support. Available on iOS and Android."
        keywords="EV driver app, EV charging app, find charging stations, fast charging near me, EV charging map, charging station locator, EV charging payments, electric vehicle app, charging station reservation"
        canonicalUrl="/drivers"
        structuredData={combinedSchema}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Ambient orbs */}
        <motion.div
          className="absolute top-1/4 right-[10%] w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ 
            background: 'radial-gradient(circle, hsl(var(--primary) / 0.12) 0%, transparent 70%)',
            filter: 'blur(60px)'
          }}
        />
        <motion.div
          className="absolute bottom-1/3 left-[5%] w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{ 
            background: 'radial-gradient(circle, hsl(var(--accent) / 0.08) 0%, transparent 70%)',
            filter: 'blur(80px)'
          }}
        />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/80 backdrop-blur-sm border border-border/30 text-primary text-sm font-medium mb-6"
                >
                  <Zap className="h-4 w-4" />
                  For EV Drivers
                </motion.div>
                
                <h1 className="text-4xl md:text-6xl font-bold mb-6">
                  Smart Charging Solutions <span className="text-gradient">for EV Drivers</span>
                </h1>
                
                <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                  Experience hassle-free electric vehicle charging with our user-friendly app—designed to make every charge simple, smart, and seamless.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button variant="hero" size="lg" className="group" asChild>
                      <a href="https://apps.apple.com/us/app/evgateway/id1202982043" target="_blank" rel="noopener noreferrer">
                        <AppleLogo className="h-5 w-5 mr-2" />
                        Download for iOS
                      </a>
                    </Button>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button variant="outline" size="lg" className="border-foreground/30 text-foreground hover:bg-foreground/10 hover:border-foreground/50" asChild>
                      <a href="https://play.google.com/store/apps/details?id=com.EVGATEWAY&hl=en_US" target="_blank" rel="noopener noreferrer">
                        <Play className="h-5 w-5 mr-2 fill-current" />
                        Download for Android
                      </a>
                    </Button>
                  </motion.div>
                </div>
              </div>
            </ScrollReveal>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                <img 
                  src={mobileApp} 
                  alt="EVGateway Mobile App"
                  className="w-64 mx-auto drop-shadow-2xl"
                />
                <img 
                  src={mobileApp2} 
                  alt="EVGateway App Features"
                  className="absolute -right-8 top-12 w-56 drop-shadow-2xl"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* App Features */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Mobile App <span className="text-gradient">Features</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-3xl mx-auto">
                Unlock a seamless and intuitive EV charging experience with our easy-to-use app—designed to give you real-time access to charger availability, flexible payment options, and total control over your charging journey.
              </p>
            </div>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" staggerDelay={0.05}>
            {appFeatures.map((feature, idx) => (
              <StaggerItem key={idx}>
                <motion.div
                  whileHover={{ y: -5, scale: 1.02 }}
                  className={`bg-background/80 backdrop-blur-sm border border-border/30 rounded-3xl p-5 text-center h-full transition-all duration-300 hover:shadow-lg ${idx % 2 === 0 ? "hover:border-primary/40 hover:shadow-primary/10" : "hover:border-accent/40 hover:shadow-accent/10"}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 ${idx % 2 === 0 ? "bg-primary/10" : "bg-accent/10"}`}>
                    <feature.icon className={`h-6 w-6 ${idx % 2 === 0 ? "text-primary" : "text-accent"}`} />
                  </div>
                  <h3 className="font-medium text-sm">{feature.name}</h3>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* How to Start Charging */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                How to Start <span className="text-gradient">Charging</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Get on the road in five simple steps.
              </p>
            </div>
          </ScrollReveal>

          <div className="max-w-4xl mx-auto">
            <StaggerContainer className="relative" staggerDelay={0.12}>
              {/* Connecting line */}
              <div className="absolute left-8 top-12 bottom-12 w-0.5 bg-gradient-to-b from-primary via-primary/50 to-primary/20 hidden md:block" />
              
              {chargingSteps.map((step, idx) => (
                <StaggerItem key={idx}>
                  <motion.div
                    whileHover={{ x: 10 }}
                    className={`flex gap-6 mb-6 last:mb-0 p-4 rounded-3xl bg-background/80 backdrop-blur-sm border border-border/30 transition-all duration-300 hover:shadow-lg ${idx % 2 === 0 ? "hover:border-primary/40 hover:shadow-primary/10" : "hover:border-accent/40 hover:shadow-accent/10"}`}
                  >
                    <div className="flex-shrink-0 relative z-10">
                      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg ${idx % 2 === 0 ? "bg-primary shadow-primary/30" : "bg-accent shadow-accent/30"}`}>
                        <step.icon className="h-8 w-8 text-primary-foreground" />
                      </div>
                    </div>
                    <div className="flex-grow pt-2">
                      <div className="flex items-center gap-4 mb-2">
                        <span className={`text-3xl font-bold ${idx % 2 === 0 ? "text-primary/30" : "text-accent/30"}`}>Step {step.step}</span>
                      </div>
                      <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                      <p className="text-muted-foreground">{step.description}</p>
                    </div>
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Frequently Asked <span className="text-gradient">Questions</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                We compiled a list of answers to address your most pressing questions regarding our mobile app.
              </p>
            </div>
          </ScrollReveal>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, idx) => (
                <AccordionItem 
                  key={idx} 
                  value={`item-${idx}`}
                  className="bg-background/80 backdrop-blur-sm border border-border/30 rounded-3xl px-6 data-[state=open]:border-primary/40 transition-all"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-5">
                    <span className="font-semibold">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Download CTA */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-primary/5 to-accent/10" />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full pointer-events-none"
          style={{ 
            background: 'radial-gradient(circle, hsl(var(--primary) / 0.15) 0%, transparent 70%)',
            filter: 'blur(80px)'
          }}
        />
        
        <div className="container mx-auto px-4 relative z-10">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Smartphone className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Ready to Start Charging?
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Download the EVGateway app today and join thousands of EV drivers enjoying seamless charging across North America.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button variant="hero" size="lg" asChild>
                    <a href="https://apps.apple.com/us/app/evgateway/id1202982043" target="_blank" rel="noopener noreferrer">
                      <AppleLogo className="h-5 w-5 mr-2" />
                      App Store
                    </a>
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button variant="outline" size="lg" className="border-foreground/30 text-foreground hover:bg-foreground/10 hover:border-foreground/50" asChild>
                    <a href="https://play.google.com/store/apps/details?id=com.EVGATEWAY&hl=en_US" target="_blank" rel="noopener noreferrer">
                      <Play className="h-5 w-5 mr-2 fill-current" />
                      Google Play
                    </a>
                  </Button>
                </motion.div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Drivers;